/**
 * main.js — loaded on every page
 *
 * Cyclo3 header interactions: search dropdown + mobile menu.
 *
 * Enqueued globally via inc/setup/enqueue-assets.php.
 * Add page-specific scripts in src/js/pages/ and enqueue conditionally:
 *   whytheme_enqueue_script('pages/home');
 */

function initCycloHeader() {
    const searchToggle = document.querySelector('[data-cyclo-search-toggle]');
    const searchDropdown = document.querySelector('[data-cyclo-search]');
    const searchInput = document.querySelector('[data-cyclo-search-input]');
    const mobileToggle = document.querySelector('[data-cyclo-mobile-toggle]');
    const mobileMenu = document.querySelector('[data-cyclo-mobile-menu]');

    const closeSearch = () => {
        if (!searchDropdown) return;
        searchDropdown.classList.add('hidden');
        searchToggle?.setAttribute('aria-expanded', 'false');
    };

    const closeMobileMenu = () => {
        if (!mobileMenu) return;
        mobileMenu.classList.add('hidden');
        mobileToggle?.setAttribute('aria-expanded', 'false');
    };

    if (searchToggle && searchDropdown) {
        searchToggle.addEventListener('click', () => {
            const isOpen = !searchDropdown.classList.contains('hidden');
            if (isOpen) {
                closeSearch();
            } else {
                closeMobileMenu();
                searchDropdown.classList.remove('hidden');
                searchToggle.setAttribute('aria-expanded', 'true');
                searchInput?.focus();
            }
        });

        // Close search on outside click
        document.addEventListener('mousedown', (event) => {
            if (
                !searchDropdown.classList.contains('hidden') &&
                !searchDropdown.contains(event.target) &&
                !searchToggle.contains(event.target)
            ) {
                closeSearch();
            }
        });
    }

    if (mobileToggle && mobileMenu) {
        mobileToggle.addEventListener('click', () => {
            const isOpen = !mobileMenu.classList.contains('hidden');
            if (isOpen) {
                closeMobileMenu();
            } else {
                closeSearch();
                mobileMenu.classList.remove('hidden');
                mobileToggle.setAttribute('aria-expanded', 'true');
            }
        });
    }

    // Close everything on ESC
    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            closeSearch();
            closeMobileMenu();
        }
    });
}

/**
 * Accordions — global (works on any page, supports multiple groups).
 * Each [data-cyclo-faq] container manages its own items (one open at a time).
 * The initial open state comes from the server-rendered `.is-open` class.
 */
function initAccordions() {
    const containers = document.querySelectorAll('[data-cyclo-faq]');
    if (!containers.length) return;

    // Respect prefers-reduced-motion: skip animation, just toggle instantly.
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /**
     * Open an accordion item smoothly.
     * Sets an explicit px height so CSS `transition: height 0.2s ease` fires correctly.
     */
    function openItem(item) {
        const answer = item.querySelector('.cyclo-faq-answer');
        if (!answer) return;

        item.classList.add('is-open');

        if (prefersReduced) {
            answer.style.height = 'auto';
            return;
        }

        // Measure the natural height, then animate to it.
        answer.style.height = answer.scrollHeight + 'px';

        // Once the transition finishes, switch to 'auto' so the panel can
        // reflow freely (e.g. if window is resized or content changes).
        answer.addEventListener('transitionend', function onOpen() {
            answer.removeEventListener('transitionend', onOpen);
            if (item.classList.contains('is-open')) {
                answer.style.height = 'auto';
            }
        });
    }

    /**
     * Close an accordion item smoothly.
     * Snapshots the current rendered height first so the transition
     * starts from a real pixel value (not "auto") before collapsing to 0.
     */
    function closeItem(item) {
        const answer = item.querySelector('.cyclo-faq-answer');
        if (!answer) return;

        if (prefersReduced) {
            item.classList.remove('is-open');
            answer.style.height = '0';
            return;
        }

        // Snapshot the live height so the CSS transition has a real start value.
        answer.style.height = answer.offsetHeight + 'px';

        // Force a reflow so the browser registers the explicit height before
        // we set it to 0 (otherwise the transition won't fire).
        // eslint-disable-next-line no-unused-expressions
        answer.offsetHeight;

        item.classList.remove('is-open');
        answer.style.height = '0';
    }

    containers.forEach((container) => {
        const items = Array.from(container.querySelectorAll('.cyclo-faq-item'));

        // Initialise any server-rendered open items (height = 'auto').
        items.forEach((item) => {
            const answer = item.querySelector('.cyclo-faq-answer');
            if (!answer) return;
            if (item.classList.contains('is-open')) {
                answer.style.height = 'auto';
            }
        });

        items.forEach((item) => {
            const toggle = item.querySelector('[data-cyclo-faq-toggle]');
            if (!toggle) return;

            toggle.addEventListener('click', () => {
                const wasOpen = item.classList.contains('is-open');

                // Close every open sibling first.
                items.forEach((other) => {
                    if (other !== item && other.classList.contains('is-open')) {
                        closeItem(other);
                        other.querySelector('[data-cyclo-faq-toggle]')
                            ?.setAttribute('aria-expanded', 'false');
                    }
                });

                if (wasOpen) {
                    closeItem(item);
                    toggle.setAttribute('aria-expanded', 'false');
                } else {
                    openItem(item);
                    toggle.setAttribute('aria-expanded', 'true');
                }
            });
        });
    });
}

/**
 * Blog "load more" — progressive enhancement.
 * The button is a real link to the next archive page; JS intercepts it,
 * fetches that page, appends its cards, and advances the link (or removes it).
 */
function initLoadMore() {
    const button = document.querySelector('.js-load-more');
    const grid = document.querySelector('.js-blog-grid');
    if (!button || !grid) return;

    button.addEventListener('click', async (event) => {
        event.preventDefault();
        const url = button.getAttribute('href');
        if (!url || button.dataset.loading === '1') return;

        button.dataset.loading = '1';
        button.setAttribute('aria-busy', 'true');
        const label = button.querySelector('span');
        const original = label ? label.textContent : '';
        if (label) label.textContent = '…';

        try {
            const response = await fetch(url, { headers: { 'X-Requested-With': 'fetch' } });
            if (!response.ok) throw new Error(String(response.status));
            const markup = await response.text();
            const doc = new DOMParser().parseFromString(markup, 'text/html');

            doc.querySelectorAll('.js-blog-grid .js-blog-card').forEach((card) => {
                grid.appendChild(document.importNode(card, true));
            });

            const nextButton = doc.querySelector('.js-load-more');
            if (nextButton && nextButton.getAttribute('href')) {
                button.setAttribute('href', nextButton.getAttribute('href'));
            } else {
                button.remove();
            }
        } catch (error) {
            // Fall back to normal navigation if the fetch fails.
            window.location.href = url;
        } finally {
            button.dataset.loading = '0';
            button.removeAttribute('aria-busy');
            if (label && original) label.textContent = original;
        }
    });
}

/**
 * Newsletter modal — site-wide CF7 form driven by Theme Settings → Popup.
 *
 * The markup ([data-cyclo-popup]) is rendered whenever a CF7 shortcode is set.
 *
 * Opening:
 *   - On click of any newsletter CTA: [data-cyclo-newsletter-open], or a link
 *     whose URL ends in #newsletter (so editors can wire menus, WYSIWYG links
 *     and ACF link fields without code). Also on page load with #newsletter.
 *   - Optionally by itself (data-auto="1"): time / scroll / exit-intent trigger,
 *     suppressed for N days after a manual close and forever after a submit.
 *     Clicking a CTA always opens it, regardless of suppression.
 *
 * Storage keys (localStorage):
 *   cyclo_popup_subscribed        '1' once the CF7 form was submitted → never auto-open again
 *   cyclo_popup_dismissed_until   epoch ms until which the modal won't auto-open after a close
 */
function initNewsletterPopup() {
    const popup = document.querySelector('[data-cyclo-popup]');
    if (!popup) return;

    const card = popup.querySelector('[data-cyclo-popup-card]');
    const closeBtn = popup.querySelector('[data-cyclo-popup-close]');
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const CTA_SELECTOR = '[data-cyclo-newsletter-open], a[href$="#newsletter"]';

    let opened = false;
    let openedAutomatically = false;
    let lastFocused = null;

    function open({ auto = false } = {}) {
        if (opened) return;
        opened = true;
        openedAutomatically = auto;
        cleanupTriggers();
        lastFocused = document.activeElement;
        popup.removeAttribute('hidden');
        void popup.offsetWidth; // force reflow so the CSS transition fires
        popup.classList.add('is-open');
        document.body.classList.add('cyclo-popup-open');
        if (closeBtn && typeof closeBtn.focus === 'function') closeBtn.focus();
    }

    function hide() {
        popup.setAttribute('hidden', '');
        document.body.classList.remove('cyclo-popup-open');
        if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    function close() {
        if (!opened) return;
        opened = false;
        popup.classList.remove('is-open');
        if (prefersReduced) {
            hide();
            return;
        }
        popup.addEventListener('transitionend', function onEnd(event) {
            if (event.target !== popup) return;
            popup.removeEventListener('transitionend', onEnd);
            hide();
        });
    }

    // Manual close. Only an auto-opened modal starts the "don't nag" window;
    // closing one the user asked for shouldn't change future behaviour.
    function dismiss() {
        if (openedAutomatically) {
            const frequencyDays = parseInt(popup.dataset.frequency || '7', 10);
            try {
                if (frequencyDays > 0) {
                    localStorage.setItem(
                        'cyclo_popup_dismissed_until',
                        String(Date.now() + frequencyDays * 24 * 60 * 60 * 1000)
                    );
                }
            } catch (e) { /* ignore storage errors */ }
        }
        close();
    }

    // --- CTA clicks (delegated, so CTAs anywhere on the page work) ---
    document.addEventListener('click', (event) => {
        const cta = event.target.closest(CTA_SELECTOR);
        if (!cta || popup.contains(cta)) return;
        event.preventDefault();
        open();
    });

    closeBtn?.addEventListener('click', dismiss);

    // Click on the backdrop (outside the card) closes the modal.
    popup.addEventListener('click', (event) => {
        if (event.target === popup) dismiss();
    });

    // ESC closes; Tab is trapped inside the card while open.
    document.addEventListener('keydown', (event) => {
        if (!opened) return;
        if (event.key === 'Escape') {
            dismiss();
            return;
        }
        if (event.key === 'Tab' && card) {
            const focusables = card.querySelectorAll(
                'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
            );
            if (!focusables.length) return;
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        }
    });

    // On successful CF7 submit: never auto-open again, then close after a short
    // beat so the user still sees the form's success message.
    document.addEventListener('wpcf7mailsent', (event) => {
        if (!popup.contains(event.target)) return;
        try { localStorage.setItem('cyclo_popup_subscribed', '1'); } catch (e) { /* ignore */ }
        window.setTimeout(close, 2500);
    });

    // --- Optional auto-open ("Αυτόματη εμφάνιση") ---
    let timer = null;
    const onScroll = () => {
        const scrollPercent = parseInt(popup.dataset.scroll || '50', 10);
        const scrollable = document.documentElement.scrollHeight - window.innerHeight;
        const pct = scrollable > 0 ? (window.scrollY / scrollable) * 100 : 100;
        if (pct >= scrollPercent) open({ auto: true });
    };
    const onMouseLeave = (event) => {
        if (event.clientY <= 0) open({ auto: true });
    };

    function cleanupTriggers() {
        if (timer) { window.clearTimeout(timer); timer = null; }
        window.removeEventListener('scroll', onScroll);
        document.removeEventListener('mouseleave', onMouseLeave);
    }

    function armAutoOpen() {
        if (popup.dataset.auto !== '1' || opened) return;

        // Respect the mobile toggle.
        if (popup.dataset.mobile === '0' && window.matchMedia('(max-width: 767px)').matches) return;

        // Suppression checks — wrapped because localStorage can throw (private mode).
        try {
            if (localStorage.getItem('cyclo_popup_subscribed') === '1') return;
            const dismissedUntil = parseInt(localStorage.getItem('cyclo_popup_dismissed_until') || '0', 10);
            if (dismissedUntil && Date.now() < dismissedUntil) return;
        } catch (e) { /* storage unavailable — treat as not suppressed */ }

        const trigger = popup.dataset.trigger || 'time';
        if (trigger === 'time') {
            const delay = parseInt(popup.dataset.delay || '5', 10);
            timer = window.setTimeout(() => open({ auto: true }), Math.max(0, delay) * 1000);
        } else if (trigger === 'scroll') {
            window.addEventListener('scroll', onScroll, { passive: true });
            onScroll(); // fire immediately if the page is already scrolled past the threshold
        } else if (trigger === 'exit') {
            // Exit-intent is a desktop behaviour (no mouseleave on touch devices).
            document.addEventListener('mouseleave', onMouseLeave);
        }
    }

    // Deep link: /any-page/#newsletter opens it on load.
    if (window.location.hash === '#newsletter') {
        open();
        history.replaceState(null, '', window.location.pathname + window.location.search);
    }

    armAutoOpen();
}

/**
 * Image lightbox — opt-in per image via [data-cyclo-lightbox="<full url>"]
 * (e.g. accordion images with the ACF "Lightbox" toggle on).
 *
 * Uses a single native <dialog>, created on first use: showModal() gives us
 * ESC-to-close, focus trapping and an inert page for free. Delegated, so
 * triggers inside closed accordions work too.
 */
function initLightbox() {
    if (!document.querySelector('[data-cyclo-lightbox]')) return;

    let dialog = null;
    let img = null;

    function build() {
        dialog = document.createElement('dialog');
        dialog.className = 'cyclo-lightbox';
        dialog.setAttribute('aria-label', 'Μεγέθυνση εικόνας');
        dialog.innerHTML = `
            <button type="button" class="cyclo-lightbox__close" aria-label="Κλείσιμο">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </button>
            <img class="cyclo-lightbox__img" alt="">
        `;
        img = dialog.querySelector('img');
        dialog.querySelector('.cyclo-lightbox__close').addEventListener('click', () => dialog.close());
        // Click anywhere except the image itself closes it.
        dialog.addEventListener('click', (event) => {
            if (event.target !== img) dialog.close();
        });
        dialog.addEventListener('close', () => {
            document.body.classList.remove('cyclo-lightbox-open');
            img.removeAttribute('src');
        });
        document.body.appendChild(dialog);
    }

    document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-cyclo-lightbox]');
        if (!trigger) return;
        event.preventDefault();
        if (!dialog) build();
        img.src = trigger.dataset.cycloLightbox;
        img.alt = trigger.dataset.cycloLightboxAlt || '';
        dialog.showModal();
        document.body.classList.add('cyclo-lightbox-open');
    });
}

function init() {
    initCycloHeader();
    initAccordions();
    initLoadMore();
    initNewsletterPopup();
    initLightbox();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
